// frontend/src/pages/Home/Home.js
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ToolCard from '../../components/ToolCard/ToolCard';
import api from '../../services/api';

const Home = () => {
  // Get i18n instance to find the current language
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [expandedCategories, setExpandedCategories] = useState({
    'Text tools': false,
    'Image tools': false,
    'Video tools': false,
    'Audio tools': false,
  });
  const [activeCategory, setActiveCategory] = useState('All');
  const [toolsData, setToolsData] = useState(null); // Initialize with null
  const [loading, setLoading] = useState(true);

  // Get the current language code (e.g., 'en', 'de', 'fr')
  const lang = i18n.language.split('-')[0];

  useEffect(() => {
    const fetchToolsData = async () => {
      try {
        const response = await api.get('/tools/'); // Your new API endpoint
        setToolsData(response.data);
      } catch (error) {
        console.error('Error fetching tools:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchToolsData();
  }, []); // Empty dependency array means this runs once on mount

  const handleToolClick = (toolSlug) => {
    // For now, this navigates to a placeholder route.
    // This is perfect for the "empty link" requirement.
    navigate(`/tools/${toolSlug}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        {/* You can add a spinner here for a better UX */}
        <div className="text-white text-xl">Loading Tools...</div>
      </div>
    );
  }

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prevState => ({
      ...prevState,
      [categoryKey]: !prevState[categoryKey] // Flip the boolean
    }));
  };
  // Define the order in which you want the categories to appear
  const categoryOrder = ['Text tools', 'Image tools', 'Video tools', 'Audio tools'];
  
  // A small change here: we now look up category data by its internal name,
  // which is what our API provides as keys.
  const categoryInternalNames = {
    'Text tools': 'Text tools',
    'Image tools': 'Image tools',
    'Video tools': 'Video tools',
    'Audio tools': 'Audio tools',
  };
  const categoryInternalNames2 = {
    'Text tools': 'text_tools',
    'Image tools': 'image_tools',
    'Video tools': 'video_tools',
    'Audio tools': 'audio_tools',
  };

  const categoryIcons = {
    'Text tools': 'bi-pencil-square',
    'Image tools': 'bi-palette',
    'Video tools': 'bi-camera-reels',
    'Audio tools': 'bi-music-note-beamed',
  };

  if (loading) return <div className="text-white p-10">Loading...</div>;

  return (
    <div className="min-h-screen bg-brand-dark text-gray-200">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        
        {/* Welcome Section - Matches your existing code */}
         <div className="mb-8 px-2 max-w-5xl">
		<h1 className="text-3xl font-bold text-white mb-2">{t('welcome_title')}</h1>
			<p className="text-white text-sm space-y-1">
						 {t('welcome_text')}
						 <span><strong>{t('welcome_strong')}</strong></span>
						 <span>{t('welcome_text1')}</span>
						 <span><strong>{t('welcome_strong1')}</strong></span>
						 <span>{t('welcome_text2')}</span>
						 <span><strong>{t('welcome_strong2')}</strong></span>

					</p><br></br>
					<p className="text-white text-sm space-y-1">
						 {t('welcome_text3')}
						 <span><strong>{t('welcome_strong2')}</strong></span>
						 <span>{t('welcome_text4')}</span>
						 <span><strong>{t('welcome_strong4')}</strong></span>
						 <span>{t('welcome_text5')}</span>
					</p><br></br>
					<p className="text-white text-sm space-y-1">
						 {t('welcome_text6')}
						 <span><strong>{t('welcome_strong5')}</strong></span>
						 <span>{t('welcome_text7')}</span>
					</p>
				</div>

        {/* Tools Sections */}
        <div className="space-y-2"> 
          {toolsData && categoryOrder.map((categoryKey) => {
            const internalName = categoryInternalNames[categoryKey];
            const categoryData = toolsData[internalName];

            if (!categoryData || !categoryData.tools || categoryData.tools.length === 0) {
              return null;
            }
	    const visibleTools = categoryData.tools.filter(
                (tool) => tool.page_slug !== 'Ask-arXiv'
            );

            // 3. If no tools remain after filtering, hide the category entirely
            if (visibleTools.length === 0) {
              return null;
            }

            const categoryDisplayName = categoryData[`display_name_${lang}`] || categoryData.display_name_en || categoryKey;
            const isExpanded = expandedCategories[categoryKey];

            return (
              // Accordion Item Container
              <div key={internalName} className="bg-slate-700/50 border border-slate-700 rounded-lg overflow-hidden">
                
                {/* 1. Accordion Header (Clickable "One Line") */}
                <button
                  onClick={() => toggleCategory(categoryKey)}
                  className="w-full flex items-center justify-between p-3 bg-slate-700 hover:bg-slate-650 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {/* Category Icon */}
                    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-orange-500">
                      <i className={`bi ${categoryIcons[categoryKey]} text-lg`}></i>
                    </div>
                    {/* Category Title */}
                    <span className="font-bold text-white text-lg">
                      {categoryDisplayName}
                    </span>
                    {/* Count Badge */}
                    <span className="text-xs bg-slate-700 text-gray-400 px-2 py-0.5 rounded-full">
                      {visibleTools.length}
                    </span>
                  </div>

                  {/* Arrow Icon (Rotates when open) */}
                  <i className={`bi bi-chevron-down transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''} text-gray-400`}></i>
                </button>

                {/* 2. Accordion Body (The Grid) */}
                {/* We use specific logic to animate height or just conditional rendering */}
                {isExpanded && (
                  <div className="p-3 border-t border-slate-700 animate-fade-in-down">
                    {/* Dense Grid: 2 cols on mobile, up to 6 on desktop */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                      {visibleTools.map((tool) => (
                        <ToolCard
                          key={tool.id}
                          tool={tool}
                          onToolClick={handleToolClick}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Home;
